import { useEffect, useRef, useState } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useInView,
  useReducedMotion,
} from "framer-motion";
import { Cloud, Code2, BrainCircuit, ArrowUpRight, Blocks } from "lucide-react";
import Reveal from "@/components/Reveal";
import "./HomeServices.css";
import { StaggerGroup, StaggerItem } from "@/components/Stagger";
import MagneticButton from "@/components/MagneticButton";
import Counter from "@/components/Counter";
import TechMarquee from "@/components/TechMarquee";
import CTASection from "@/components/CTASection";
import TerminalCard from "@/components/TerminalCard";
import SectionKicker from "@/components/SectionKicker";
import AtmosphereHero from "@/components/AtmosphereHero";
import ParallaxLayer from "@/components/ParallaxLayer";
import FloatingParticles from "@/components/FloatingParticles";
import WordScramble from "@/components/WordScramble";
import MorphingText from "@/components/MorphingText";
import WaveText from "@/components/WaveText";
import SelectedWork from "@/components/SelectedWork";
import ProcessSection from "@/components/ProcessSection";
import { useCtaPanel } from "@/components/StartProjectPanel";
import { metrics } from "@/data/metrics";
import useMediaQuery from "@/hooks/use-media-query";
import { Link } from "react-router-dom";

const EASE = [0.22, 1, 0.36, 1];

const HERO_IMG = "/images/hero-abstract-1600.webp";
const HERO_IMG_SET =
  "/images/hero-abstract-800.webp 800w, /images/hero-abstract-1600.webp 1600w";

const heroWords = ["WE", "BUILD"];
const morphingWords = ["DIGITAL", "INTELLIGENT", "SCALABLE", "INNOVATIVE", "POWERFUL"];

// Proof strip numbers live in src/data/metrics.js (see VERIFY notes there)

const services = [
  {
    icon: Code2,
    title: "Custom Software & Apps",
    desc: "Web platforms, mobile apps and bespoke enterprise software — engineered to be fast, scalable and effortless to use.",
    span: "md:col-span-7",
  },
  {
    icon: BrainCircuit,
    title: "AI & Machine Learning",
    desc: "Custom models, fine-tuning and seamless AI integration built into your product.",
    span: "md:col-span-5",
  },
  {
    icon: Cloud,
    title: "Infrastructure, Pipelines & Automation",
    desc: "CI/CD pipelines, DevOps and workflow automation — secure, monitored and highly available by default.",
    span: "md:col-span-7",
  },
  {
    icon: Blocks,
    title: "Blockchain & Web3",
    desc: "Smart contracts, dApps and DeFi mechanics — engineered with a security-first mindset.",
    span: "md:col-span-5",
  },
];

export default function Home() {
  const heroRef = useRef(null);
  const reduced = useReducedMotion();
  const ctaPanel = useCtaPanel();
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-25%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.45, 0.9]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);

  // Touch devices: no Ken Burns / heavy atmosphere
  const touch = useMediaQuery("(hover: none)");
  // Pause hero atmosphere (orbs, particles) when the hero is off-screen
  const heroInView = useInView(heroRef, { amount: 0.15 });
  // Atmosphere mounts last in the entrance timeline (t≈1.3s)
  const [atmosphereReady, setAtmosphereReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setAtmosphereReady(true), 1300);
    return () => clearTimeout(t);
  }, []);
  const breathing = heroInView && !reduced;

  return (
    <div data-testid="home-page" className="home-page">
      <div className="grain home-page-grain" aria-hidden="true" />
      {/* HERO — min-h-screen (not h-screen) so short viewports grow instead of
          overflowing: content used to spill under the fixed header at the top
          and into the scroll indicator at the bottom */}
      <section ref={heroRef} className="relative min-h-screen overflow-hidden" data-testid="home-hero">
        {/* Background layers with enhanced parallax.
            Outer div = scroll parallax (unchanged); inner div = entrance scale. */}
        <motion.div className="absolute inset-0" style={{ y: imgY, scale }}>
          <motion.div
            className="h-full w-full"
            initial={{ scale: 1.15 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1.2, ease: EASE }}
          >
            <AtmosphereHero />
          </motion.div>
        </motion.div>
        <motion.div className="absolute inset-0 bg-ink" style={{ opacity: overlayOpacity }} />
        {/* Entrance-only overlay: starts opaque, eases out so the combined
            overlay settles at the scroll-driven resting opacity (t=0 → ~1s) */}
        <motion.div
          className="absolute inset-0 bg-ink"
          initial={{ opacity: 1 }}
          animate={{ opacity: 0 }}
          transition={{ duration: 1.0, ease: "easeOut" }}
        />
        
        {/* Multiple parallax gradient layers */}
        <ParallaxLayer speed={0.3} className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/10" />
        </ParallaxLayer>
        <ParallaxLayer speed={0.5} className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/30 to-transparent" />
        </ParallaxLayer>
        
        {/* Animated tech grid — fades in at t=0.3s */}
        <motion.div
          className="tech-grid grid-fade absolute inset-0"
          initial={{ opacity: 0 }}
          animate={{
            opacity: 0.6,
            backgroundPosition: ["0% 0%", "100% 100%"],
          }}
          transition={{
            opacity: { delay: 0.3, duration: 0.6 },
            backgroundPosition: { duration: 20, repeat: Infinity, ease: "linear" },
          }}
        />

        {/* Floating particles — mount last (t≈1.3s), dim, paused off-screen */}
        {atmosphereReady && !touch && (
          <motion.div
            className="absolute inset-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <FloatingParticles count={8} dim active={heroInView} />
          </motion.div>
        )}

        {/* Animated glow orbs — fade in at t=1.3s, breathe only while in view */}
        <motion.div
          className="absolute -top-20 right-[10%] h-[420px] w-[420px]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3, duration: 0.8 }}
        >
          <motion.div
            className="glow-orb h-full w-full"
            animate={breathing ? { opacity: [0.35, 0.7, 0.35], scale: [1, 1.1, 1] } : { opacity: 0.35 }}
            transition={
              breathing
                ? { duration: 4, repeat: Infinity, ease: "easeInOut" }
                : { duration: 0.3 }
            }
          />
        </motion.div>
        <motion.div
          className="absolute bottom-20 left-[15%] h-[320px] w-[320px]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.8 }}
        >
          <motion.div
            className="glow-orb h-full w-full"
            animate={breathing ? { opacity: [0.25, 0.5, 0.25], scale: [1, 1.15, 1] } : { opacity: 0.25 }}
            transition={
              breathing
                ? { duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }
                : { duration: 0.3 }
            }
          />
        </motion.div>

        {/* HUD corner readouts — top-24 (96px): clear of the 80px fixed header
            above, and of the hero content which starts at pt-32 (128px).

            These were pinned to the section edge (left-6 md:left-12) while every
            other element on the page sits inside max-w-7xl mx-auto. On anything
            wider than 1280px the container is centred with large margins, so the
            readouts floated far outside the headline they belong to. They now
            share the hero's own container, which is what makes them line up with
            the eyebrow above the headline at every width. */}
        <div className="pointer-events-none absolute inset-x-0 top-24 z-10 hidden sm:block">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between gap-6">
        <motion.div
          className="flex items-center gap-3 font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-400"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5, duration: 0.8 }}
        >
          <motion.span 
            className="h-1.5 w-1.5 rounded-full bg-cyan-accent"
            animate={{
              opacity: [0.5, 1, 0.5],
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          Systems operational
        </motion.div>
        <motion.div
          className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-400"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.6, duration: 0.8 }}
        >
          22.31°N · 73.18°E — Vadodara
        </motion.div>
        </div>
        </div>

        {/* Floating terminal with enhanced animation */}
        <motion.div 
          className="absolute bottom-12 left-1/2 z-20 hidden w-full max-w-7xl -translate-x-1/2 px-6 md:px-12 lg:flex lg:justify-end"
          initial={{ opacity: 0, y: 20, scale: 0.9 }}
          animate={{ 
            opacity: 1, 
            y: 0,
            scale: 1,
          }}
          transition={{ delay: 1.2, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <motion.div
            animate={{
              y: [0, -10, 0],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <TerminalCard />
          </motion.div>
        </motion.div>

        {/* pt-32 keeps the eyebrow below the fixed header; pb-36 reserves a
            bottom band so the CTA row never meets the scroll indicator */}
        <motion.div
          style={{ y: textY }}
          className="relative z-10 min-h-screen max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-center pt-28 pb-24 md:pt-32 md:pb-36"
        >
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45, duration: 0.8 }}
            className="font-mono text-xs tracking-[0.25em] uppercase text-cyan-accent mb-6"
          >
            KedByte Technologies Private Limited — Software · AI · Automation
          </motion.p>

          {/* Headline lines mask-reveal sequentially from t=0.5s */}
          <h1 className="font-heading font-black uppercase tracking-tight text-white leading-[0.85] text-6xl sm:text-7xl md:text-8xl lg:text-[8.5rem]">
            <span className="sr-only">
              KedByte — custom software development company in India:{" "}
            </span>
            {heroWords.map((w, i) => (
              <span key={w} className="block overflow-hidden">
                <motion.span
                  className="inline-block"
                  initial={{ y: "110%", rotateX: -90 }}
                  animate={{ y: 0, rotateX: 0 }}
                  transition={{ delay: 0.5 + 0.12 * i, duration: 0.9, ease: EASE }}
                >
                  {w}
                </motion.span>
              </span>
            ))}
            <span className="block overflow-hidden">
              {/* Flat #00F0FF fill, tracking-tight: no glow class — at this
                  size the halo sums across near-touching capitals and reads
                  as a band; the pure fill carries the brand */}
              <motion.span
                className="inline-block text-cyan-accent"
                initial={{ y: "110%", rotateX: -90 }}
                animate={{ y: 0, rotateX: 0 }}
                transition={{
                  y: { delay: 0.74, duration: 0.9, ease: EASE },
                  rotateX: { delay: 0.74, duration: 0.9, ease: EASE },
                }}
              >
                {/* Starts cycling only after its line has fully revealed (~1.65s) */}
                <MorphingText words={morphingWords} interval={2000} startDelay={1700} />
              </motion.span>
            </span>
            <span className="block overflow-hidden">
              <motion.span
                className="inline-block"
                initial={{ y: "110%", rotateX: -90 }}
                animate={{ y: 0, rotateX: 0 }}
                transition={{ delay: 0.86, duration: 0.9, ease: EASE }}
              >
                FUTURES
              </motion.span>
            </span>
          </h1>

          {/* Sub-copy and CTA fade-up with stagger from t=1.0s */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0, duration: 0.8, ease: EASE }}
            className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-6"
          >
            <motion.p
              className="max-w-md text-zinc-300 text-base md:text-lg leading-relaxed"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.1 }}
            >
              An ultra-minimal custom software development company in India —
              engineering intelligent, high-performance products from interface
              to AI to infrastructure.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.18, duration: 0.6, ease: EASE }}
            >
              <MagneticButton onClick={() => ctaPanel.open()} testid="home-hero-cta">
                Start a project
              </MagneticButton>
            </motion.div>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 font-mono text-[10px] tracking-[0.3em] uppercase text-zinc-400"
        >
          <motion.div
            animate={{ y: [0, 5, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          >
            Scroll to explore
          </motion.div>
        </motion.div>
      </section>

      {/* MARQUEE */}
      <section className="py-12 border-y border-white/10 bg-ink overflow-hidden">
        <Reveal className="max-w-7xl mx-auto px-6 md:px-12 mb-8">
          <p className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-400 text-center">
            The stack we engineer with
          </p>
        </Reveal>
        <TechMarquee />
      </section>

      {/* SERVICES BENTO */}
      <section className="relative py-16 md:py-40 overflow-hidden" data-testid="home-services">
        <div className="tech-grid grid-fade absolute inset-0 opacity-30" />
        
        {/* Animated background elements */}
        <ParallaxLayer speed={0.2} className="absolute top-20 left-10">
          <motion.div
            className="w-64 h-64 rounded-full bg-cyan-accent/5 blur-3xl"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.5, 0.3],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        </ParallaxLayer>
        
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
            <div>
              <Reveal>
                <SectionKicker index="[01]">What we do</SectionKicker>
              </Reveal>
              <Reveal delay={0.1} variant="mask">
                <h2 className="mt-4 font-heading font-bold tracking-tighter text-4xl md:text-5xl text-white max-w-2xl">
                  Capabilities engineered for scale.
                </h2>
              </Reveal>
            </div>
            <Reveal delay={0.2}>
              <motion.div whileHover={{ x: 5 }} transition={{ type: "spring", stiffness: 400 }}>
                <Link
                  to="/services"
                  className="group inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
                  data-testid="home-services-link"
                >
                  All services
                  <motion.div
                    whileHover={{ rotate: 45 }}
                    transition={{ type: "spring", stiffness: 400 }}
                  >
                    <ArrowUpRight className="h-4 w-4" />
                  </motion.div>
                </Link>
              </motion.div>
            </Reveal>
          </div>

          <div className="grid md:grid-cols-12 gap-4 md:gap-5">
            {services.map((s, i) => (
              <Link
                key={s.title}
                to="/services"
                data-testid={`home-service-card-${i}`}
                data-cursor-text="VIEW"
                className={`home-service-card ${s.span}`}
              >
                <s.icon className="home-service-icon" strokeWidth={1.5} aria-hidden="true" focusable="false" />
                <h3 className="font-heading">{s.title}</h3>
                <p>{s.desc}</p>
                <span className="home-service-explore">
                  Explore <ArrowUpRight size={18} aria-hidden="true" focusable="false" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* SELECTED WORK */}
      <SelectedWork />

      {/* PROCESS */}
      <ProcessSection />

      {/* PROOF STRIP (data: src/data/metrics.js) */}
      <section className="relative py-16 md:py-32 border-y border-white/10 bg-surface/40 overflow-hidden" data-testid="home-stats">
        <div className="tech-grid grid-fade absolute inset-0 opacity-20" />
        
        {/* Animated scanline effect */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-accent/5 to-transparent h-32"
          animate={{
            y: ["0%", "400%"],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "linear"
          }}
        />
        
        <StaggerGroup className="relative max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-4 gap-12">
          {metrics.map((s) => (
            <StaggerItem key={s.label}>
              <motion.div
                whileHover={{ 
                  scale: 1.05,
                  transition: { type: "spring", stiffness: 400, damping: 20 }
                }}
              >
                <div className="relative pl-5 group cursor-default">
                  {/* Hairline divider draws itself as the section enters */}
                  <motion.span
                    className="absolute left-0 top-0 h-full w-px origin-top bg-white/10"
                    initial={{ scaleY: 0 }}
                    whileInView={{ scaleY: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, ease: EASE, delay: 0.15 }}
                  />
                  <div className="font-heading font-black tracking-tighter text-4xl md:text-6xl text-white">
                    <Counter to={s.to} prefix={s.prefix} suffix={s.suffix} decimals={s.decimals} />
                  </div>
                  <motion.p 
                    className="mt-3 font-mono text-xs tracking-wider uppercase text-zinc-400 group-hover:text-cyan-accent transition-colors"
                  >
                    {s.label}
                  </motion.p>
                </div>
              </motion.div>
            </StaggerItem>
          ))}
        </StaggerGroup>
      </section>

      {/* PAYROLL TEASER — minimal, honest product strip */}
      <section className="border-b border-white/10" data-testid="home-payroll-teaser">
        <Reveal className="max-w-7xl mx-auto px-6 md:px-12">
          <Link
            to="/payroll"
            className="group flex flex-col md:flex-row md:items-center justify-between gap-3 py-7"
            data-testid="home-payroll-link"
          >
            <div className="flex flex-wrap items-center gap-4">
              <span className="rounded-full border border-cyan-accent/40 bg-cyan-accent/5 px-3 py-1 font-mono text-[10px] tracking-[0.25em] uppercase text-cyan-accent">
                New
              </span>
              <span className="text-zinc-300">
                <span className="font-heading font-bold text-white">KedByte Payroll</span>
                {" "}— UK payroll software for bureaux, in development.
              </span>
            </div>
            <span className="inline-flex items-center gap-2 text-sm text-zinc-400 group-hover:text-cyan-accent transition-colors">
              Early access
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </span>
          </Link>
        </Reveal>
      </section>

      {/* WORD SCRAMBLE - Interactive Service Discovery */}
      <section className="relative py-16 md:py-32 bg-ink border-y border-white/10 overflow-hidden">
        <div className="tech-grid grid-fade absolute inset-0 opacity-20" />
        <Reveal className="relative max-w-7xl mx-auto px-6 md:px-12">
          <WordScramble />
        </Reveal>
      </section>

      <CTASection />
    </div>
  );
}
